import { z } from 'zod'
import { OrganizationSchema, DealInviteDomainSchema } from '../schemas'
import { http } from './http'
import { auth } from './auth'
import { DealStatus } from '../types'
import { parseWithZod } from '../utils/parse-with-zod'

const OrganizationResponseSchema = z.object({
  organizations: z.array(OrganizationSchema),
})

const OrganizationSingleResponseSchema = z.object({
  organization: OrganizationSchema,
})

const ModifyDealStatusResponseSchema = z.object({
  organization: OrganizationSchema,
})

const ModifyClientMatterResponseSchema = z.object({
  organization: OrganizationSchema,
})

const DealInviteDomainsResponseSchema = z.object({
  deal_invite_domains: z.array(DealInviteDomainSchema),
})

type ModifyDealStatusPayload = {
  deal_status_rule: 'enabled' | 'disabled' | null
  deal_status_required: boolean | null
  deal_status_english_label: boolean | null
  deal_status_french_label: boolean | null
  deal_statuses: DealStatus[] | null
}

type DeletedDealStatusPayload = {
  deleted_deal_statuses: string[]
}

type ModifyClientNumberPayload = {
  client_number_state: 'enabled' | 'disabled' | 'required' | null
}

type ModifyMatterNumberPayload = {
  matter_number_state: 'enabled' | 'disabled' | 'required' | null
}

type UpdateInviteDomainRulePayload = {
  deal_invite_domain_rule: 'enabled' | 'disabled'
}

export const organizations = {
  async getByAuthId(orgId: number) {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/organizations`, {
      auth_organization_id: orgId,
    })

    const parsedResult = parseWithZod(OrganizationResponseSchema, result)
    return parsedResult.organizations[0]
  },

  async getById(orgId: number, regionHost?: string) {
    const host = regionHost ? `${location.protocol}//${regionHost}` : auth.getUserRegionHost()
    const result = await http.get(`${host}/api/organizations/${orgId}`)

    const parsedResult = parseWithZod(OrganizationSingleResponseSchema, result)
    return parsedResult.organization
  },

  async modifyDealStatuses(orgId: number, payload: ModifyDealStatusPayload) {
    const regionHost = auth.getUserRegionHost()

    const result = await http.post(
      `${regionHost}/api/organizations/${orgId}/modify_deal_statuses`,
      payload,
    )
    const parsedResult = parseWithZod(ModifyDealStatusResponseSchema, result)
    return parsedResult.organization
  },

  async modifyClientNumberState(orgId: number, payload: ModifyClientNumberPayload) {
    const regionHost = auth.getUserRegionHost()

    const result = await http.post(
      `${regionHost}/api/organizations/${orgId}/set_client_number_state`,
      payload,
    )
    const parsedResult = parseWithZod(ModifyClientMatterResponseSchema, result)
    return parsedResult.organization
  },

  async modifyMatterNumberState(orgId: number, payload: ModifyMatterNumberPayload) {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/organizations/${orgId}/set_matter_number_state`,
      payload,
    )
    const parsedResult = parseWithZod(ModifyClientMatterResponseSchema, result)
    return parsedResult.organization
  },

  async modifySystemClientNumberState(orgId: number, payload: ModifyClientNumberPayload) {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/organizations/${orgId}/set_system_client_number_state`,
      payload,
    )
    const parsedResult = parseWithZod(ModifyClientMatterResponseSchema, result)
    return parsedResult.organization
  },

  async modifySystemMatterNumberState(orgId: number, payload: ModifyMatterNumberPayload) {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/organizations/${orgId}/set_system_matter_number_state`,
      payload,
    )
    const parsedResult = parseWithZod(ModifyClientMatterResponseSchema, result)
    return parsedResult.organization
  },

  async deleteDealStatuses(orgId: number, deletedDealStatuses: DeletedDealStatusPayload) {
    const regionHost = auth.getUserRegionHost()

    const result = await http.post(
      `${regionHost}/api/organizations/${orgId}/delete_deal_statuses`,
      deletedDealStatuses,
    )

    return result
  },

  async updateInviteDomainRule(orgId: number, payload: UpdateInviteDomainRulePayload) {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/organizations/${orgId}/update_deal_invite_domain_rule`,
      payload,
    )
    const parsedResult = parseWithZod(OrganizationSingleResponseSchema, result)
    return parsedResult.organization
  },

  async getDealInviteDomains(orgId: number) {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/organizations/${orgId}/deal_invite_domains`)
    return parseWithZod(DealInviteDomainsResponseSchema, result).deal_invite_domains
  },

  async bulkAddDealInviteDomains(orgId: number, payload: { deal_invite_domains: string[] }) {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/organizations/${orgId}/bulk_add_deal_invite_domains`,
      payload,
    )
    return parseWithZod(DealInviteDomainsResponseSchema, result).deal_invite_domains
  },

  async deleteDealInviteDomains(orgId: number, domainIds: number[]) {
    const regionHost = auth.getUserRegionHost()
    return http.post(`${regionHost}/api/organizations/${orgId}/delete_deal_invite_domains`, {
      deal_invite_domains: domainIds,
    })
  },
}
